import { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Tooltip } from "react-tooltip5";
import moment from "moment";
import { filterDictonary } from "@routes/intel/dashboard/const";
import { filterTypeDictionary } from "@core/global/global-country-dictionary";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { main_dropdown_options } from "@containers/shipments/components/RightPanel/components/SaveSearch/utility";
import { hasOwn } from "@core/utility/utility";
import commaFormat from "@utils/commaFormat";

// Match-type labels (`queryType`) — mirrors MatchTypeEnum in the saved-search DetailedView.
const MATCH_TYPE_LABELS: Record<string, string> = {
  contains: "Contains",
  exact: "Exact Phrase",
};

const COMPANY_SEARCH_KEY = "company_search";

// Tailwind class groups mirroring the saved-search "Applied Filters" layout.
const PILL_CLASS = "tw-flex tw-w-max tw-flex-row tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-gray-200 tw-px-3 tw-py-1 tw-text-sm tw-leading-5 tw-text-gray-900";
const CATEGORY_TITLE_CLASS = "tw-flex tw-items-center tw-text-sm tw-font-medium tw-leading-5 tw-text-gray-600";
const CATEGORY_CONTAINER_CLASS = "tw-flex tw-flex-col tw-gap-2";
const PILL_CONTAINER_CLASS = "tw-flex tw-flex-wrap tw-items-stretch tw-gap-3";
const SECTION_HEADING_CLASS = "tw-text-sm tw-font-medium tw-leading-5 tw-text-gray-900";
const SECTION_CLASS = "tw-flex tw-w-1/2 tw-flex-col tw-items-stretch tw-gap-5 tw-p-3";

type FilterDef = { key?: string; altKey?: string; additionalKey?: string; additionalType?: string; type?: string; label?: string };

type WorkspaceListCriteriaModalProps = {
  show: boolean;
  onHide: () => void;
  workspaceName?: string;
  searchPayload?: Record<string, unknown> | null;
};

// Resolve the country filter list — same source the saved-search "Applied Filters" view uses.
// The workspace search_payload carries `dataSource` (e.g. "us imports"); each country in
// globalCountryDetailsDictionary exposes that same dataSource plus a redirectionID that matches
// the filterDictonary keys (e.g. "us_imports").
const resolveCountry = (dataSource: unknown): { filterList: FilterDef[]; countryName: string } => {
  const ds = Array.isArray(dataSource) ? dataSource[0] : dataSource;
  const dict = filterDictonary as Record<string, FilterDef[]>;
  if (!ds) return { filterList: dict.world_imports ?? [], countryName: "Multi Country" };
  if (ds === "multi_country") return { filterList: dict.world_imports ?? [], countryName: "Multi Country" };
  const match = Object.values(globalCountryDetailsDictionary as Record<string, { dataSource?: string; redirectionID?: string; countryName?: string }>).find((country) => country?.dataSource === ds);
  const key = match?.redirectionID;
  return { filterList: (key && dict[key]) || [], countryName: match?.countryName ?? "" };
};

// The filters a Hermes list keeps in search_payload, read from the user's question by the
// workspace backend (hermesWorkspaceSearch.py). Values are stored lowercase.
const HERMES_FILTERS = [
  { key: "originCountries", label: "Country of Export" },
  { key: "destinationCountries", label: "Country of Import" },
  { key: "originRegions", label: "Region of Export" },
  { key: "destinationRegions", label: "Region of Import" },
  { key: "portsOfLading", label: "Port of Lading" },
  { key: "portsOfUnlading", label: "Port of Unlading" },
  { key: "transportModes", label: "Mode of Transport" },
  { key: "buyerStates", label: "Buyer State" },
  { key: "companyCountries", label: "Company Country" },
  { key: "tradingPartners", label: "Trading Partner" },
];

const toTitleCase = (value: string): string => value.replace(/\b\w/g, (letter) => letter.toUpperCase());

const cleanName = (value: unknown): string => {
  const s = value === null || value === undefined ? "" : String(value);
  return s.includes("$$") ? s.split("$$")[0] : s;
};

// Determines whether a workspace has any criteria worth surfacing (drives the info-icon visibility).
export const hasWorkspaceListCriteria = (searchPayload?: Record<string, unknown> | null): boolean => !!searchPayload && typeof searchPayload === "object" && Object.keys(searchPayload).length > 0;

const WorkspaceListCriteriaModal = ({ show, onHide, workspaceName, searchPayload }: WorkspaceListCriteriaModalProps) => {
  const ft = filterTypeDictionary;
  const data = (searchPayload ?? {}) as Record<string, unknown>;

  const { filterList, countryName } = useMemo(() => resolveCountry(data.dataSource), [data.dataSource]);

  const includeFilters = Array.isArray(data.includeFilters) ? (data.includeFilters as string[]) : [];
  const excludeFilters = Array.isArray(data.excludeFilters) ? (data.excludeFilters as string[]) : [];
  const suffixFor = (key?: string): string => (key && includeFilters.includes(key) ? " (Included)" : key && excludeFilters.includes(key) ? " (Excluded)" : "");

  // Searched details (left section). Lists created from company profile / company search /
  // bulk company search / Hermes are stored with key "company_search".
  const isCompanySearch = data.key === COMPANY_SEARCH_KEY;
  const isHermes = data.source === "hermes";
  const searchCategory = isHermes ? "Hermes" : isCompanySearch ? "Search Companies" : (main_dropdown_options[String(data.key ?? "")] ?? "");
  const searchPhrase = data.search ? String(data.search) : "";
  const matchType = MATCH_TYPE_LABELS[String(data.queryType ?? "")] ?? "";
  // Lists created from company profile / company search / bulk company search (stored with
  // lookbackYears: 1) or Hermes (lookbackYears: 2) cover that many years up to the multi-country
  // latest date; others show their search dates.
  const lookbackYears = data.lookbackYears === 1 || data.lookbackYears === 2 ? data.lookbackYears : null;
  const dateRange = lookbackYears ? `Last ${lookbackYears} ${lookbackYears === 1 ? "year" : "years"}` : data.fromDate && data.toDate ? `${moment(data.fromDate as string).format("MMM DD, YYYY")} - ${moment(data.toDate as string).format("MMM DD, YYYY")}` : "";

  // Value pills — same truncation / "+N" overflow behaviour as the saved-search view.
  const renderPills = (values: unknown[], type?: string) => {
    if (type === ft.multiSelectRangeFilter) {
      return values.map((ele) => {
        const obj = ele as { key?: unknown; range?: unknown[] };
        const range = Array.isArray(obj?.range) ? obj.range : [];
        return (
          <span key={String(obj?.key)} className={PILL_CLASS}>
            {String(obj?.key)} : {range.length === 2 ? `${commaFormat(range[0])} to ${commaFormat(range[1])}` : ""}
          </span>
        );
      });
    }
    const head = values.slice(0, 3).map((ele, index) => {
      const display = cleanName(ele);
      const truncated = display.length > 14 ? `${display.slice(0, 14)}...` : display;
      return (
        <span key={`${display}-${index}`} className={PILL_CLASS} data-tooltip-id="workspace-criteria-tooltip" data-tooltip-content={display.length > 14 ? display : undefined}>
          {truncated}
        </span>
      );
    });
    const rest =
      values.length > 3 ? (
        <span
          className={PILL_CLASS}
          data-tooltip-id="workspace-criteria-tooltip"
          data-tooltip-content={values
            .slice(3)
            .map((v) => cleanName(v))
            .join(", ")}>
          +{values.length - 3}
        </span>
      ) : null;
    return (
      <>
        {head}
        {rest}
      </>
    );
  };

  // Hermes lists: the filters read from the user's question ("exporting steel from China
  // by sea" → Country of Export: China, Mode of Transport: Maritime).
  const hermesFilterBlocks = isHermes
    ? HERMES_FILTERS.map(({ key, label }) => {
        const values = data[key];
        if (!Array.isArray(values) || values.length === 0) return null;
        return (
          <div className={CATEGORY_CONTAINER_CLASS} key={`hermes-${key}`}>
            <div className={CATEGORY_TITLE_CLASS}>{label}</div>
            <div className={PILL_CONTAINER_CLASS}>{renderPills(values.map((value) => toTitleCase(String(value))))}</div>
          </div>
        );
      }).filter(Boolean)
    : [];

  // Applied filters (right section) — mirrors DetailedView's per-filter-type handling.
  const appliedFilterBlocks = [
    ...hermesFilterBlocks,
    ...(filterList || [])
      .map((def) => {
        if (!def) return null;

        if (def.type === ft.radioFilter) {
          const v = data[def.key as string];
          const value = v === true && hasOwn(data, "include_master_bill") ? "Exclude" : v === true && hasOwn(data, "is_enriched_data") ? "Complete Bills of Lading" : v === false && hasOwn(data, "is_enriched_data") ? "Partial & Complete Bill of Lading" : "Include";
          return (
            <div className={CATEGORY_CONTAINER_CLASS} key={`radio-${def.key}`}>
              <div className={CATEGORY_TITLE_CLASS}>{def.label}</div>
              <div className={PILL_CONTAINER_CLASS}>
                <span className={PILL_CLASS}>{value}</span>
              </div>
            </div>
          );
        }

        if (def.type === ft.localizeFilter) {
          const arr = data[def.altKey as string];
          if (!Array.isArray(arr) || arr.length === 0) return null;
          return (
            <div className={CATEGORY_CONTAINER_CLASS} key={`localize-${def.altKey}`}>
              <div className={CATEGORY_TITLE_CLASS}>
                {def.label}
                {suffixFor(def.altKey)}
              </div>
              <div className={PILL_CONTAINER_CLASS}>{renderPills(arr)}</div>
            </div>
          );
        }

        const primary = data[def.key as string];
        if (Array.isArray(primary) && primary.length > 0) {
          return (
            <div className={CATEGORY_CONTAINER_CLASS} key={`primary-${def.key}`}>
              <div className={CATEGORY_TITLE_CLASS}>
                {def.label}
                {suffixFor(def.key)}
              </div>
              <div className={PILL_CONTAINER_CLASS}>{renderPills(primary, def.type)}</div>
            </div>
          );
        }

        const additional = data[def.additionalKey as string];
        if (Array.isArray(additional) && additional.length > 0) {
          return (
            <div className={CATEGORY_CONTAINER_CLASS} key={`additional-${def.additionalKey}`}>
              <div className={CATEGORY_TITLE_CLASS}>
                {def.label}
                {suffixFor(def.additionalKey)}
              </div>
              <div className={PILL_CONTAINER_CLASS}>{renderPills(additional, def.additionalType === "multiSelectRangeFilter" ? ft.multiSelectRangeFilter : def.additionalType)}</div>
            </div>
          );
        }

        return null;
      })
      .filter(Boolean),
  ];

  return (
    <Modal centered size="lg" show={show} onHide={onHide}>
      <Modal.Header closeButton className="tw-border-b tw-border-gray-200 tw-px-5 tw-py-3">
        <div>
          <Modal.Title className="tw-text-lg tw-font-semibold tw-text-gray-900">List creation criteria</Modal.Title>
          {(workspaceName || countryName) && <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-gray-500">{[workspaceName, countryName].filter(Boolean).join(" • ")}</p>}
        </div>
      </Modal.Header>
      <Modal.Body className="tw-p-0">
        <div className="tw-flex">
          <div className={SECTION_CLASS}>
            <div className={SECTION_HEADING_CLASS}>Searched Details</div>
            {searchCategory && (
              <div className={CATEGORY_CONTAINER_CLASS}>
                <div className={CATEGORY_TITLE_CLASS}>Search Category</div>
                <div className={PILL_CONTAINER_CLASS}>
                  <span className={PILL_CLASS}>{searchCategory}</span>
                </div>
              </div>
            )}
            {/* Company-search lists have no search phrase (the phrase was a company name);
                Hermes lists show the one extracted from the user's question, when there is one. */}
            {(!isCompanySearch || (isHermes && searchPhrase)) && (
              <div className={CATEGORY_CONTAINER_CLASS}>
                <div className={CATEGORY_TITLE_CLASS}>Search Phrase</div>
                <div className={PILL_CONTAINER_CLASS}>
                  {searchPhrase ? (
                    searchPhrase.includes("||") ? (
                      searchPhrase.split("||").map((phrase, index) => (
                        <span key={`${phrase}-${index}`} className={PILL_CLASS}>
                          {phrase || "NA"}
                        </span>
                      ))
                    ) : (
                      <span className={PILL_CLASS}>{searchPhrase}</span>
                    )
                  ) : (
                    <span className={PILL_CLASS}>NA</span>
                  )}
                </div>
              </div>
            )}
            {matchType && !isCompanySearch && (
              <div className={CATEGORY_CONTAINER_CLASS}>
                <div className={CATEGORY_TITLE_CLASS}>Match Type</div>
                <div className={PILL_CONTAINER_CLASS}>
                  <span className={PILL_CLASS}>{matchType}</span>
                </div>
              </div>
            )}
            <div className={CATEGORY_CONTAINER_CLASS}>
              <div className={CATEGORY_TITLE_CLASS}>Date Range</div>
              <div className={PILL_CONTAINER_CLASS}>
                <span className={PILL_CLASS}>{dateRange || "NA"}</span>
              </div>
            </div>
          </div>
          <div className={`${SECTION_CLASS} tw-border-l tw-border-gray-200`}>
            <div className={SECTION_HEADING_CLASS}>Applied Filters</div>
            {appliedFilterBlocks.length > 0 ? appliedFilterBlocks : <span className="tw-text-sm tw-text-gray-400">No additional filters were applied.</span>}
          </div>
        </div>
      </Modal.Body>
      <Tooltip id="workspace-criteria-tooltip" className="tw-z-[1100] tw-max-w-[300px] !tw-opacity-100" />
    </Modal>
  );
};

export default WorkspaceListCriteriaModal;
